/**
 * 檔案用途區塊
 * @module WalletConnectLogin
 * @purpose 負責 WalletConnect SSO 登入流程，包含開啟 AppKit 連線彈窗、監聽 SDK 事件、完成多鏈簽名與授權資料編碼後導向登入轉接頁。
 * @external https://innotech.atlassian.net/browse/IN-111885
 * @external https://innotech.atlassian.net/browse/IN-111892
 * @external https://innotech.atlassian.net/browse/IN-113493
 * @external https://innotech.atlassian.net/browse/IN-120071
 * @external https://innotech.atlassian.net/browse/IN-121035
 * @external https://innotech.atlassian.net/browse/IN-124251
 * @external https://innotech.atlassian.net/browse/IN-124642
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAppKit, useAppKitState, modal as appKitModal, useAppKitAccount } from '@reown/appkit/react';
import { useThirdPartyProvider } from '@shared/models';
import { WalletConnectAuthPayload } from '@utilities/helpers/encryption.helper';
import { isUserRejectedError } from '@modules/walletconnect/helpers/payment-handlers';
import { signMessageByChain } from '@modules/walletconnect/helpers/multi-chain-signature.helper';
import {
  replaceRandomCodePlaceholder,
  resolveWalletConnectSignatureDisplayValue,
} from '@modules/walletconnect/config/owner-code-random-mapping';
import { useStrictSessionCheck } from '@modules/walletconnect/hooks/useStrictSessionCheck';
import { useWalletConnectI18n } from '@modules/walletconnect/hooks/useWalletConnectI18n';
import { ThirdpartyLoginMethod } from '@shared/enums';

/**
 * 宣告內容用途說明與單號關聯
 * @description 將任意可序列化資料以 UTF-8 安全流程轉為 Base64 字串。
 * @purpose 避免直接使用 btoa 處理非 Latin1 字元時發生 InvalidCharacterError，確保簽名授權內容可穩定編碼。
 * @external https://innotech.atlassian.net/browse/IN-111890
 */
const encodeBase64Utf8 = (value: unknown): string => {
  const json = JSON.stringify(value);
  const encoder = new TextEncoder();
  const bytes = encoder.encode(json);

  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i] as number);
  }

  return window.btoa(binary);
};

/**
 * 宣告內容用途說明與單號關聯
 * @description WalletConnect 登入主元件，處理 session 檢查、彈窗啟動、事件監聽、簽名與登入導向。
 * @purpose 在第三方登入流程中整合 AppKit 與多鏈簽名能力，將登入證明封裝後交由 redirect handler 接續處理。
 * @external https://innotech.atlassian.net/browse/IN-111885
 */
const WalletConnectLogin: React.FC = () => {
  // 防線一：嚴格 Session 檢查（SSO 入口門禁）
  useStrictSessionCheck();

  const { payload } = useThirdPartyProvider();
  const { open } = useAppKit();
  const { initialized } = useAppKitState();
  const { address, isConnected, caipAddress } = useAppKitAccount();

  // 載入 i18n 翻譯
  const { t } = useWalletConnectI18n(payload.lang);

  const [error, setError] = useState<string | null>(null);
  const [loginHandled, setLoginHandled] = useState(false);
  const hasTriggeredOpenRef = useRef(false);

  const handleLogin = useCallback(async () => {
    try {
      /**
       * 1. 透過 AppKit 打開連線 modal，讓使用者選擇錢包並完成連線。
       * 實際的簽名與加密會放在下面的 useEffect 中，在偵測到已連線後再執行。
       */
      await open({ view: isConnected ? 'Account' : 'Connect' });
    } catch (e) {
      const error = e instanceof Error ? e : new Error(String(e));

      // 針對使用者拒絕簽署的情境給予較友善的提示
      if (isUserRejectedError(error)) {
        // 使用者已拒絕簽署訊息
        return;
      }
    }
  }, [open, isConnected]);

  // 以 SDK 事件驅動：連線成功切換到 Account 視圖；關閉彈窗且尚未完成登入則導回 redirectUrl（IN-120071）
  useEffect(() => {
    if (!appKitModal || !payload.redirectUrl) {
      return;
    }

    const unsubscribe = appKitModal.subscribeEvents((eventState) => {
      const data = eventState?.data;
      if (!data || data.type !== 'track') {
        return;
      }

      const evt = data.event;

      if (evt === 'CONNECT_SUCCESS') {
        // 連線成功：切換到 Account 視圖，讓使用者看到錢包資訊並等待 APP 授權
        open({ view: 'Account' });
      }

      const url = payload.redirectUrl;
      if (data.event === 'MODAL_CLOSE') {
        const connected = data.properties.connected;

        if (!connected) {
          // 未連線就關閉彈窗 → 導回 redirectUrl
          if (!loginHandled && url) {
            window.location.replace(url);
          }
        } else {
          // 已連線但 SDK 關閉了彈窗（例如連線完成後自動關）→ 重新打開 Account 視圖，避免白畫面
          if (!loginHandled) {
            open({ view: 'Account' });
          }
        }
      }

      if (evt === 'USER_REJECTED' && url) {
        window.location.replace(url);
      }
    });

    return () => unsubscribe();
  }, [payload.redirectUrl, open, t, loginHandled]);

  /**
   * 監聽 AppKit 狀態，一旦偵測到錢包已有地址（代表錢包連線完成），
   * 再進行 SIWE 訊息組裝、簽名、JWE 加密與 redirect。
   *
   * 注意：此函數支援多鏈（EVM 和 Bitcoin），會根據 caipAddress 的 namespace
   * 自動選擇對應的簽名方法。
   */
  useEffect(() => {
    if (loginHandled) {
      return;
    }

    const { clientId, redirectUrl } = payload;
    if (!clientId || !redirectUrl) {
      return;
    }

    const appKitInstance = appKitModal;
    if (!appKitInstance) {
      return;
    }

    if (!address || !caipAddress) {
      // 尚未連線完成，等待下一次 state 變化
      return;
    }

    const run = async () => {
      // 簽名文案顯示：優先 OPS `WALLET_CONNECT_DISPLAY_DOMAIN`，否則業主對應亂數代碼
      const signatureDisplayValue = resolveWalletConnectSignatureDisplayValue(
        window._env_?.VENDERID ?? location.origin,
      );

      // 依需求格式組裝簽名文案：OPS 代碼／亂數代碼、說明、備註、錢包地址
      const messageLines = [
        replaceRandomCodePlaceholder(t('siwe.loginRequest'), signatureDisplayValue),
        t('siwe.signaturePurpose'),
        '',
        t('siwe.note'),
        replaceRandomCodePlaceholder(t('siwe.confirmRequestOrigin'), signatureDisplayValue),
        '',
        t('siwe.walletAddress'),
        address,
      ];
      const message = messageLines.join('\n');

      /**
       * 使用多鏈簽名輔助函數，根據 caipAddress 的 namespace 自動選擇對應的簽名方法
       * - EVM 鏈：使用 ethers.signMessage()
       * - Bitcoin：使用 bitcoin_signMessage 或 btc_signMessage
       * - 其他鏈：拋出錯誤（尚未實作）
       */
      try {
        // 使用多鏈簽名輔助函數
        const { signature } = await signMessageByChain(message, caipAddress);

        // 將登入證明打包成 payload
        const payloadToEncrypt: WalletConnectAuthPayload = {
          message,
          signature,
          caipAddress,
        };

        // 原本使用 JWE 加密的方式（已註解，後端改為使用 base64 編碼）
        // const authCode = await encryptAuthData(payloadToEncrypt);
        // 改用 base64 編碼（UTF-8 安全版本，避免 btoa 遇到非 Latin1 字元時拋出 InvalidCharacterError）
        const authCode = encodeBase64Utf8(payloadToEncrypt);

        // 跳轉到 Redirect 組件處理（與其他登入方式一致）
        // Redirect 組件會讀取 URL 參數並跳轉到最終的 redirectUrl
        const redirectToHandler = `${window.location.origin}?provider=${ThirdpartyLoginMethod.WalletConnect}&authCode=${encodeURIComponent(
          authCode,
        )}&redirectUrl=${encodeURIComponent(redirectUrl)}`;

        setLoginHandled(true);
        window.location.replace(redirectToHandler);
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err ?? ''));

        // 檢查是否為使用者拒絕簽名
        if (isUserRejectedError(error) && redirectUrl) {
          setError(t('errors.userCancelled'));
          window.location.replace(redirectUrl);
          return;
        }

        const errorMessage = error.message || t('errors.unknownError');

        // 其他錯誤
        setError(`${t('errors.signingFailed')} ${errorMessage}`);
        console.error(error);
      }
    };

    run();
  }, [loginHandled, payload.clientId, payload.redirectUrl, address, caipAddress]);

  // 僅在首次 initialized 時打開彈窗，避免「關閉→導頁」時因 handleLogin 參考變動再次觸發 open 造成閃爍
  useEffect(() => {
    if (!initialized || hasTriggeredOpenRef.current) {
      return;
    }
    hasTriggeredOpenRef.current = true;
    void handleLogin();
  }, [initialized, handleLogin]);

  return <></>;
};

export default WalletConnectLogin;

/**
 * === llm 分析紀錄區 ===
 * @llm-review-submitted-at 2026-06-14T07:16:14.169Z
 * @llm-review-model gpt-5.3-codex
 * @llm-review-note Added required three-section annotation layout (top/module, middle/declaration, bottom/llm record), converted declaration comments to required tag format, and attached Jira browse URLs using declarationOrigins tickets only where applicable. Runtime logic remains unchanged.
 */
